import MapStackedItems from "@/components/mapping/MapStackedItems.vue";
import MarkerInfoWindow from "@/components/mapping/MarkerInfoWindow.vue";
import { MapState } from "@/store/map/types";
import {
	CloseClickResponse,
	ClusterContextMenuResponse,
	IInfoWindowOptions,
	MapEventListenerEventNames,
	MarkerContextMenuResponse,
	MapLayerItemType as MapLayerItemTypeEnum
} from "@sureview/v2-mapping-saas";
import ObjectTypeId from "../types/sv-data/enums/ObjectTypeIds";
import { Md5 } from "ts-md5";
import { Component, Vue, Watch } from "vue-property-decorator";
import { CombinedVueInstance } from "vue/types/vue";
import { namespace } from "vuex-class";

const MapStore = namespace("map");

/**
 * @summary Mixin which handles the map info windows.
 */
@Component
export default class MapInfoWindowMixin extends Vue {
    @MapStore.State((state: MapState) => state.markerContextMenuResponse) protected readonly markerContextMenuResponse: MarkerContextMenuResponse | null;
    @MapStore.State((state: MapState) => state.clusterContextMenuResponse) public readonly clusterContextMenuResponse: ClusterContextMenuResponse | null;
    @MapStore.State((state: MapState) => state.closeInfoWindow) public readonly closeCurrentInfoWindow: boolean;
    @MapStore.Mutation protected setMarkerContextMenuResponse: (markerContextMenuResponse: MarkerContextMenuResponse | null) => void;
    @MapStore.Mutation protected setClusterContextMenuResponse: (clusterContextMenuResponse: ClusterContextMenuResponse | null) => void;
    @MapStore.Mutation protected setCloseInfoWindow: (close: boolean) => void;

    public readonly infoWindowPixelOffsetPin: number = -50;
    public readonly infoWindowPixelOffsetCentred: number = -30;

    private infoWindowComponent: CombinedVueInstance<Record<never, any> & Vue, object, object, object, Record<never, any>> | null = null;

    private mapLayerItemTypeToObjectIdMapping: Record<MapLayerItemTypeEnum, ObjectTypeId | null> = {
        [MapLayerItemTypeEnum.Camera]: ObjectTypeId.Camera,
        [MapLayerItemTypeEnum.Audio]: ObjectTypeId.Audio,
        [MapLayerItemTypeEnum.Relay]: ObjectTypeId.Door,
        [MapLayerItemTypeEnum.Alarm]: ObjectTypeId.Alarm,
        [MapLayerItemTypeEnum.Output]: ObjectTypeId.Relay,
        // Add other mappings here
    };

    /**
     * @summary The info windows to be displayed on the map.
     */
    public infoWindowOptions: IInfoWindowOptions[] = [];

    /**
     * @summary Whether the host map allows device control from the pop-over. Overridden by hosts that opt in; default off.
     */
    protected get allowDeviceControl(): boolean {
        return false;
    }

	/**
	 * @summary method to close the info window and reset the context menu response.
	 */
	protected resetInfoWindows(resetClusterContextMenuResponse: boolean, resetMarkerContextMenuResponse: boolean): void {
		if (resetClusterContextMenuResponse || resetMarkerContextMenuResponse) {
			this.closeInfoWindow();
		}

		if (resetClusterContextMenuResponse) {
			this.setClusterContextMenuResponse(null);
		}

		if (resetMarkerContextMenuResponse) {
			this.setMarkerContextMenuResponse(null);
		}
	}

	/**
     * @summary Closes the info window(s).
     */
	private closeInfoWindow(): void {
		this.infoWindowComponent?.$destroy();
		this.infoWindowComponent = null;
		this.infoWindowOptions = [];
		this.setCloseInfoWindow(false);
	}

    /**
     * @summary Handler for changes to markerContextMenuResponse.
     */
    @Watch("markerContextMenuResponse")
    private onMarkerContextMenuResponseChanged(
		newValue: MarkerContextMenuResponse | null,
		oldValue: MarkerContextMenuResponse | null
	): void {
        if (newValue && (!oldValue || (oldValue && newValue.key !== oldValue.key))) {
            this.onContextMenu();
        }
    }

    /**
     * @summary Handler for changes to clusterContextMenuResponse.
     */
    @Watch("clusterContextMenuResponse")
    private onClusterContextMenuResponseChanged(): void {
        if (this.clusterContextMenuResponse) {
            this.onClusterContextMenu();
        }
    }

    /**
     * @summary Handler for changes to closeCurrentInfoWindow.
     */
    @Watch("closeCurrentInfoWindow")
    private onCloseCurrentInfoWindowChanged(): void {
        if (this.closeCurrentInfoWindow) {
            this.closeInfoWindow();
        }
    }

    /**
     * @summary Handler for when contextmenu event is raised by a map marker.
     */
    private onContextMenu(): void {
		// close info window if it is already open and
		// reset the cluster context menu response
		this.resetInfoWindows(true, false);

        let markerInfoWindowConstructor = Vue.extend(MarkerInfoWindow);
        this.infoWindowComponent = new markerInfoWindowConstructor({
            store: this.$store,
            propsData: {
                itemInfo: {
                    mapLayerItemId: this.markerContextMenuResponse.mapLayerItemId,
                    mapLayerItemType: this.markerContextMenuResponse.mapLayerItemType,
                    objectId: this.markerContextMenuResponse.objectId,
                    title: this.markerContextMenuResponse.title,
					objectTypeId: this.objectIdForMapLayerItemType
                },
                allowDeviceControl: this.allowDeviceControl
            }
        }).$mount();

        this.infoWindowOptions = [
            {
                key: this.markerContextMenuResponse.key,
                content: this.infoWindowComponent.$el,
                position: this.markerContextMenuResponse.latLng,
                pixelOffset: {
                    width: 0,
                    height: this.infoWindowPixelOffsetPin,
                    equals: null
                },
				minWidth: 500,
                mapEventListeners: [
                    // CloseClick triggers when the info window is closed. We need to tell the map to remove the info window from its service when closed.
                    {
                        eventName: MapEventListenerEventNames.CloseClick,
                        callback: (_: CloseClickResponse) => {
							// Reset both context menu responses when the info window is closed.
                           	this.resetInfoWindows(true, true);
                        },
                    },
                ],
            },
        ];
    }

    /**
     * @summary Handler for when contextMenu event is raised by a map cluster.
     */
    private onClusterContextMenu(): void {
		// close info window if it is already open and
		// reset the marker context menu response
		this.resetInfoWindows( false, true);

        let mapStackedItemsConstructor = Vue.extend(MapStackedItems);
        this.infoWindowComponent = new mapStackedItemsConstructor({
            store: this.$store,
        }).$mount();

        let headerElement = document.createElement("h4");
        headerElement.style.paddingLeft = "10px";
        headerElement.textContent = "Contents of cluster";

        this.infoWindowOptions = [
            {
                key: Md5.hashStr(JSON.stringify(this.clusterContextMenuResponse)),
                headerContent: headerElement,
                content: this.infoWindowComponent.$el,
                position: this.clusterContextMenuResponse.latLng,
                pixelOffset: {
                    width: 0,
                    height: this.infoWindowPixelOffsetCentred,
                    equals: null
                },
                maxWidth: 250,
                mapEventListeners: [
                    // CloseClick triggers when the info window is closed. We need to tell the map to remove the info window from its service when closed.
                    {
                        eventName: MapEventListenerEventNames.CloseClick,
                        callback: (_: CloseClickResponse) => {
							// Reset both context menu responses when the info window is closed.
							this.resetInfoWindows(true, true);
                        },
                    },
                ],
            },
        ];
    }

	/**
	 * @summary Returns the ObjectTypeId for the map layer item type.
	 * @returns
	 *  - ObjectTypeId for the map layer item type if it exists.
	 *  - null if the map layer item type does not have a corresponding ObjectTypeId.
	 */
	private get objectIdForMapLayerItemType(): ObjectTypeId | null {
		return this.mapLayerItemTypeToObjectIdMapping[this.markerContextMenuResponse?.mapLayerItemType] || null;
	}
}
